<template>
    <div class="media-upload-field">
        <!-- Has a URL → show preview + actions -->
        <div v-if="modelValue" class="preview">
            <img
                v-if="kind === 'image'"
                :src="modelValue"
                :alt="`${kind} preview`"
                class="preview-media"
            />
            <video
                v-else-if="isDirectVideo(modelValue)"
                :src="modelValue"
                class="preview-media"
                controls
                preload="metadata"
            />
            <div v-else class="preview-embed">
                <i class="pi pi-link" />
                <a
                    :href="modelValue"
                    target="_blank"
                    rel="noopener noreferrer"
                    >{{ modelValue }}</a
                >
            </div>

            <div class="preview-actions">
                <Button
                    label="Replace"
                    icon="pi pi-refresh"
                    size="small"
                    severity="secondary"
                    text
                    @click="reset"
                    :disabled="isUploading"
                />
                <Button
                    label="Remove"
                    icon="pi pi-times"
                    size="small"
                    severity="danger"
                    text
                    @click="clear"
                    :disabled="isUploading"
                />
            </div>
        </div>

        <!-- No URL → upload widget + URL paste fallback -->
        <div v-else class="empty">
            <FileUpload
                mode="basic"
                :accept="acceptAttr"
                :maxFileSize="maxBytes"
                :auto="true"
                customUpload
                :chooseLabel="`Upload ${kind}`"
                chooseIcon="pi pi-upload"
                @uploader="onUploader"
                :disabled="isUploading"
                class="upload-button"
            />
            <Button
                label="Paste URL instead"
                icon="pi pi-link"
                size="small"
                severity="secondary"
                text
                @click="showUrlPaste = !showUrlPaste"
                :disabled="isUploading"
            />
            <InputText
                v-if="showUrlPaste"
                :modelValue="urlDraft"
                @update:modelValue="urlDraft = $event ?? ''"
                @blur="commitUrlDraft"
                @keyup.enter="commitUrlDraft"
                :placeholder="urlPlaceholder"
                class="url-input"
            />
            <small v-if="isUploading" class="uploading-hint">
                <i class="pi pi-spin pi-spinner" /> Uploading…
            </small>
        </div>
    </div>
</template>

<script setup lang="ts">
import FileUpload, { type FileUploadUploaderEvent } from "primevue/fileupload";
import InputText from "primevue/inputtext";
import { useToast } from "primevue/usetoast";
import { computed, ref } from "vue";

import { useOrganizationManager } from "@/composables/organization-manager";
import { useAuthenticationStore } from "@/stores/authentication-store";

const props = withDefaults(
    defineProps<{
        modelValue: string;
        kind: "image" | "video";
        maxBytes?: number;
        urlPlaceholder?: string;
    }>(),
    {
        maxBytes: 50 * 1024 * 1024,
        urlPlaceholder: "https://…",
    }
);

const emit = defineEmits<{
    "update:modelValue": [value: string];
}>();

const toast = useToast();
const { organization } = useOrganizationManager();
const { getToken } = useAuthenticationStore();

const isUploading = ref(false);
const showUrlPaste = ref(false);
const urlDraft = ref("");

const acceptAttr = computed(() =>
    props.kind === "image"
        ? "image/png,image/jpeg,image/webp,image/gif"
        : "video/mp4,video/webm"
);

const isDirectVideo = (url: string) =>
    /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(url);

const setUrl = (url: string) => {
    emit("update:modelValue", url);
    urlDraft.value = "";
    showUrlPaste.value = false;
};

const reset = () => setUrl("");
const clear = () => setUrl("");

const commitUrlDraft = () => {
    const trimmed = urlDraft.value.trim();
    if (trimmed) setUrl(trimmed);
};

const onUploader = async (event: FileUploadUploaderEvent) => {
    const file = Array.isArray(event.files) ? event.files[0] : event.files;
    if (!file || !organization.value) return;

    isUploading.value = true;
    try {
        const token = await getToken();
        if (!token) throw new Error("Authentication required.");

        const formData = new FormData();
        formData.append("file", file);

        const baseUrl = import.meta.env.VITE_API_URL;
        const response = await fetch(
            `${baseUrl}/whats_new/whats_new_mgt/uploads`,
            {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    Organization: organization.value.identity,
                },
                body: formData,
            }
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const detail = errorData.detail?.[0];
            const message =
                typeof detail === "string"
                    ? detail
                    : (detail?.message ?? errorData.detail);
            throw new Error(
                typeof message === "string"
                    ? message
                    : `Upload failed: ${response.statusText}`
            );
        }

        const { url } = (await response.json()) as { url: string };
        setUrl(url);
        toast.add({
            severity: "success",
            summary: "Uploaded",
            detail: "File uploaded.",
            life: 2000,
        });
    } catch (error) {
        console.error("Upload failed:", error);
        toast.add({
            severity: "error",
            summary: "Upload failed",
            detail: error instanceof Error ? error.message : "Unknown error",
            life: 5000,
        });
    } finally {
        isUploading.value = false;
    }
};
</script>

<style scoped lang="scss">
.media-upload-field {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.empty {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;

    .upload-button {
        flex: 0 0 auto;
    }

    .url-input {
        flex: 1 1 100%;
    }
}

.preview {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.5rem;
    border: 1px solid var(--surface-border);
    border-radius: var(--border-radius, 4px);
    background: var(--surface-section);
}

.preview-media {
    max-width: 100%;
    max-height: 200px;
    object-fit: contain;
    border-radius: 4px;
    background: var(--surface-ground);
}

.preview-embed {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    overflow: hidden;
    color: var(--text-color-secondary);
    font-size: 0.85rem;

    a {
        color: var(--stekz-primary-color);
        text-decoration: none;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}

.preview-actions {
    display: flex;
    gap: 0.25rem;
    justify-content: flex-end;
}

.uploading-hint {
    color: var(--text-color-secondary);
    font-size: 0.8rem;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
}
</style>
